import Backbone from 'backbone';
import $ from 'jquery';
import _ from 'underscore';

import * as alignmentcomments from 'analyses/alignment/AlignmentComments';
import {setAllTrimsFromSelection, setTrimFromSelection} from 'analyses/alignment/AlignmentSetTrimHandlers';
import GapSequenceWrapper from 'analyses/alignment/GapSequenceWrapper';
import EditPrimerTabState from 'analyses/EditPrimerTabState';
import * as analytics from 'analytics/Analytics';
import asm from 'assembly/Asm';
import {assert} from 'Asserts';
import {StandardFeature} from 'auth/capabilities/CapabilityConstants';
import {MolbioFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import * as biojs from 'BioJS';
import {
  CodonTranslationTable,
  DNA_COMPLEMENT_TABLE,
  NUCLEOTIDE_TYPE_TO_COMPLEMENT_TABLE,
} from 'biojs/BioConstants';
import {Part} from 'biojs/Part';
import * as util from 'biojs/Util';
import {
  canDeleteOrReplacePartAtSelectionStart,
  canEditPartAtSelectionStart,
  canOpenPartAtSelectionStart,
  canUnlinkPartAtSelectionStart,
  getSinglePart,
  getTranslationMenuActionName,
  hasAlignmentModel,
  MenuChoicesOptions,
  shouldShowMultiPartMenu,
  shouldShowPartMenu,
  toggleCaseHandler,
  trackChoice,
  trackCreatePrimer,
  trackOpenAnnotationPanel,
  trackOpenTranslationPanel,
  translateCodon,
} from 'components/SelectionContextMenuHelpers';
import {ComponentsSequence} from 'components/Sequence';
import {TrimSideOption} from 'constants/AlignmentConstants';
import {MOLBIO_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {convertStrandToDisplayName, StrandOfDoubleStranded} from 'constants/MolbioConstants';
import {SequenceType} from 'constants/SequenceConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import FileStates from 'FileStates';
import {TranslationBiochemicalPropertiesTabState} from 'molbio/aa-sequences/BiochemicalPropertiesTab';
import {ProteinColorScheme} from 'molbio/aa-sequences/util/AaSequenceColoringConstants';
import Annotation from 'molbio/annotations/backbone/Annotation';
import CodonTableModal from 'molbio/CodonTable/CodonTableModal';
import {CopyNucleotideSequenceMode, getDefaultCopyModeForSequence} from 'molbio/CopyUtil';
import {blastPrimer, blastSequence} from 'molbio/ExternalLib';
import AddAnnotationToFeatureLibraryModal from 'molbio/feature-libraries/AddAnnotationToFeatureLibraryModal';
import {createFromAnnotation} from 'molbio/feature-libraries/featureLibraryUtil';
import {AlignmentMapState} from 'molbio/maps/MapStates';
import SequenceMapUtil from 'molbio/maps/SequenceMapUtil';
import {handleEditFromSelection} from 'molbio/monomers/selectionContextHelpers';
// @ts-expect-error
import InsertPartModalView from 'molbio/parts/backbone/InsertPartModalView';
import {isCreateNewPartFromSelectionDisabled} from 'molbio/parts/isCreateNewPartFromSelectionDisabled';
import {canCreatePrimerPair, createPrimerPair, existingPrimerPairSelected} from 'molbio/primers/PrimerUtil';
import ReindexModal from 'molbio/ReindexModal';
import SequenceAlignmentTabState from 'molbio/tabstates/SequenceAlignmentTabState';
import {
  confirmTranslationLinkInvalidationRisk,
  getTranslationRegionGroupsForSequence,
  TranslationRegionGroupData,
} from 'molbio/translations/TranslationUtil';
import notifications from 'notifications';
import {BENCHLING_BLAST_MENU_TITLE, NCBI_BLAST_MENU_TITLE} from 'search/blast/BLASTConstants';
import {runBLASTModalForSelection} from 'search/blast/BLASTUtil';
import {validateBLASTSequence} from 'search/blast/validation';
import {Operator} from 'search/SearchConstants';
import * as vo from 'settings/visibility-options/VisibilityOptions';
import EntityStore from 'stores/EntityStore';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import legacyRunModal from 'ui-library/legacyRunModal';
import APIUtil from 'util/APIUtil';
import {BasicEvent} from 'util/Types';

import * as contextmenu from './ContextMenu';

export function openOrCreateBiochemicalPropertiesTab(
  sequence: ComponentsSequence,
  codonTranslationTable: CodonTranslationTable,
  strand: StrandOfDoubleStranded = 1,
  coerceStartCodonToMethionine: boolean = false
): void {
  const sequenceFile = FileStates.get().get(sequence.id);
  const biochemicalPropertiesTab = sequenceFile.findTab(TranslationBiochemicalPropertiesTabState);
  if (biochemicalPropertiesTab) {
    biochemicalPropertiesTab.set({active: true, strand, codonTranslationTable, coerceStartCodonToMethionine});
  } else {
    sequenceFile.buffers().addTab(
      new TranslationBiochemicalPropertiesTabState({
        polymer: sequence,
        strand,
        codonTranslationTable,
        coerceStartCodonToMethionine,
      })
    );
  }
  analytics.trackEvent(MOLBIO_ANALYTICS_ACTIONS.OPENED_BIOCHEMICAL_PROPERTIES_TAB_ON_DNA, {
    polymerId: sequence.id,
    direction: convertStrandToDisplayName(strand).toLowerCase(), // Convert to display name for back-compat.
    codonTranslationTable,
    coerceStartCodonToMethionine,
  });
}

async function addToFeatureLibraryHandler(
  sequence: ComponentsSequence,
  annotation: Annotation
): Promise<void> {
  let bases = util.slice(
    sequence.get('dna').forward(),
    annotation.start(),
    annotation.end(),
    sequence.circular()
  );
  if (annotation.strand() === -1) {
    bases = util.revcomp(bases, sequence.get('_sequence').baseTable());
  }

  const featureLibraryId = await legacyRunModal(AddAnnotationToFeatureLibraryModal, {
    annotationResidues: bases,
  });
  if (featureLibraryId != null) {
    await createFromAnnotation(annotation, sequence, featureLibraryId)
      .done(() => {
        notifications.success('Feature successfully added to library.');
      })
      .fail((res: Response) => {
        notifications.notifyError(res);
      });
  }
}

function getOpenPartMenuOption(
  sequence: ComponentsSequence | GapSequenceWrapper,
  overlappingPart: Part | null
): MenuChoice {
  return {
    name: 'Open part',
    click(): void {
      assert(
        sequence.selectionContainedInPart(),
        `Expected selection with endpoints ${sequence.selection().start()}, ${sequence
          .selection()
          .end()} to be contained in a part.`
      );
      const part = overlappingPart || getSinglePart(sequence);
      let apiId = part.sequence().get('id');

      // TODO(T1884): Remove when sequence versions are no longer stored as json.
      // A sequence version's json may have an unprefixed id, since this was not migrated when we added
      // prefixes. If the prefix is missing, we add it.
      const prefix = `${APIUtil.typeToApiPrefix(ModelTypeConstants.SEQUENCE)}_`;
      if (!apiId.startsWith(prefix)) {
        apiId = `${prefix}${apiId}`;
      }

      FileStates.get().openFolderItem(apiId);
    },
    disabled(): boolean {
      return !canOpenPartAtSelectionStart(sequence, overlappingPart);
    },
    hide(): boolean {
      return !shouldShowPartMenu(sequence, overlappingPart);
    },
  };
}

function getUnlinkPartMenuOption(
  sequence: ComponentsSequence | GapSequenceWrapper,
  overlappingPart: Part | null
): MenuChoice {
  return {
    name: 'Unlink part',
    click(): void {
      assert(
        sequence.selectionContainedInPart(),
        `Expected selection with endpoints ${sequence.selection().start()}, ${sequence
          .selection()
          .end()} to be contained in a part.`
      );
      const part = overlappingPart || getSinglePart(sequence);
      sequence.unlinkPart(part).fail((res: Response) => {
        notifications.notifyError(res);
      });
    },
    disabled(): boolean {
      return !canUnlinkPartAtSelectionStart(sequence, overlappingPart);
    },
    hide(): boolean {
      return !shouldShowPartMenu(sequence, overlappingPart);
    },
  };
}

function getSelectPartMenuOption(sequence: ComponentsSequence | GapSequenceWrapper, part: Part): MenuChoice {
  return {
    name: 'Select part',
    click(): void {
      sequence.selection().setEndpoints(part.start(), part.end());
    },
  };
}

type bioentityAwareMenuKeys = 'createRNAFromSelection' | 'createTranslation' | 'createProtein';
type MenuInteractions = Pick<MenuChoice, 'click' | 'disabled' | 'hide' | 'dataTestAttrs'>;

/**
 * Generate menu with single option or directional submenus, depending on bioentity type.
 *
 * This function was written to eliminate single option submenus on RNA sequences. RNA only has a forward
 * strand, so there were multiple selection options with submenus that contain a single option, ie:
 * "Create Translation... -> Forward". Based on the strandedness of the bioentity provided, this function
 * will return either a single action menu (single stranded), or a menu containing Forward
 * and Reverse submenus (double stranded).
 * @param menuKey: Internal representation of the menu to be generated, used to find its defaults and supported actions
 * @param isInAlignment: Whether this selection menu is being displayed in an alignment view
 * @param sequence: Sequence entity being selected.
 * @param additionalMenus: Optional, additional menus to add for double stranded entities.
 */
function buildCollapsibleMenu(
  menuKey: bioentityAwareMenuKeys,
  isInAlignment: boolean,
  sequence: ComponentsSequence | GapSequenceWrapper,
  additionalMenus?: Array<MenuChoice>
): MenuChoice {
  const isSingleStranded = sequence.isSingleStranded();

  // Track the base that each menu is built from. The values here have preference when building the final menu
  const choiceToMenuDefaults: {
    [key in bioentityAwareMenuKeys]: MenuChoice;
  } = {
    createRNAFromSelection: {
      name: 'Create RNA sequence',
      disabled(): boolean {
        return EntityStore.currentUser() == null;
      },
      hide(): boolean {
        return (
          !isFeatureEnabled({feature: StandardFeature.CREATE, itemType: ModelTypeConstants.SEQUENCE}) ||
          sequence.isOligo()
        );
      },
      dataTestAttrs: {component: 'ContextMenu', element: 'createRNAFromSelection'},
    },
    createTranslation: {
      name: 'Create translation...',
      hide(): boolean {
        return !isFeatureEnabled({feature: MolbioFeature.MOLBIO_EDIT}) || !sequence.supportsTranslations();
      },
      dataTestAttrs: {component: 'ContextMenu', element: 'createTranslation'},
    },
    createProtein: {
      name: 'Create AA sequence...',
      hide(): boolean {
        return (
          !isFeatureEnabled({feature: StandardFeature.CREATE, itemType: ModelTypeConstants.PROTEIN}) ||
          sequence.isOligo()
        );
      },
      dataTestAttrs: {component: 'ContextMenu', element: 'createProtein'},
    },
  };

  function generateRNACreationChoice(strand: 1 | -1, isInAlignment: boolean): MenuInteractions {
    return {
      click(): void {
        sequence.handleCreateFromSelectionClick(SequenceType.SINGLE_STRANDED, strand);
      },
      disabled(): boolean {
        return EntityStore.currentUser() == null;
      },
      hide(): boolean {
        return !isFeatureEnabled({
          feature: StandardFeature.CREATE,
          itemType: ModelTypeConstants.SEQUENCE,
        });
      },
      dataTestAttrs: {
        component: 'ContextMenu',
        element: strand === 1 ? 'createForwardRNASequence' : 'createReverseRNASequence',
      },
    };
  }

  function generateTranslationCreationChoice(strand: 1 | -1, isInAlignment: boolean): MenuInteractions {
    return {
      async click(): Promise<void> {
        if (!isInAlignment) {
          trackOpenTranslationPanel(sequence, strand);
        }
        await sequence.createTranslation(strand);
      },
      disabled(): boolean {
        return !sequence.allowFeatureEdits();
      },
      dataTestAttrs: {
        component: 'ContextMenu',
        element: strand === 1 ? 'createForwardTranslation' : 'createReverseTranslation',
      },
    };
  }

  function generateProteinCreationChoice(strand: 1 | -1, isInAlignment: boolean): MenuInteractions {
    return {
      click(): void {
        sequence.handleCreateProteinFromSelectionClick(strand);
      },
      disabled(): boolean {
        return EntityStore.currentUser() == null;
      },
      hide(): boolean {
        return !isFeatureEnabled({feature: StandardFeature.CREATE, itemType: ModelTypeConstants.PROTEIN});
      },
      dataTestAttrs: {
        component: 'ContextMenu',
        element: strand === 1 ? 'createForwardProtein' : 'createReverseProtein',
      },
    };
  }

  // Associate a given menuKey with a function to generate its actions. For single stranded bioentities,
  // the return value from these functions is merged with the default menu to create a flat menu. For double
  // stranded entities, these functions create menus in the forward and reverse direction.
  const choiceToInteractionFn: {
    [key in bioentityAwareMenuKeys]: (strand: 1 | -1, isInAlignment: boolean) => MenuInteractions;
  } = {
    createRNAFromSelection: generateRNACreationChoice,
    createTranslation: generateTranslationCreationChoice,
    createProtein: generateProteinCreationChoice,
  };

  let menuDefaults = choiceToMenuDefaults[menuKey];

  // A different, more specific, set of translation options is permitted in Alignment views
  const translationInAlignment = menuKey === 'createTranslation' && isInAlignment;
  const menuInteractionCreationFn = choiceToInteractionFn[menuKey];
  // Single Stranded bioentities: Collapse submenus into a single base menu, give preference to default values.
  if (isSingleStranded && !translationInAlignment) {
    menuDefaults = {
      ...menuInteractionCreationFn(1, isInAlignment),
      ...menuDefaults,
      // remove ellipses when menu is collapsed into single menuDefaults
      name: menuDefaults.name.replace('...', ''),
    };
    // Double Stranded bioentities: add new menu to base, create forward and reverse options in that menu
  } else {
    let menuInteractions = [
      {...menuInteractionCreationFn(1, isInAlignment), name: 'Forward'},
      // Hide reverse selection for RNA in alignment views
      {
        ...menuInteractionCreationFn(-1, isInAlignment),
        name: 'Reverse',
        hide(): boolean {
          return isSingleStranded;
        },
      },
    ];
    if (additionalMenus) {
      menuInteractions = menuInteractions.concat(additionalMenus);
    }
    menuDefaults = {
      ...menuDefaults,
      menu: menuInteractions,
    };
  }
  return menuDefaults;
}

export interface MenuChoice {
  name: string;
  id?: string;
  menu?: Array<MenuChoice | '-'>;

  click?(): void | Promise<void>;

  disabled?(): boolean;

  hide?(): boolean;

  close?(focus: HTMLElement): void;

  copy?(): string | void;

  dataTestAttrs?: DataTestAttrs;
}

/**
 * Returns the concatenated bases for all regions of the given translation group in the forward
 * direction, along with the translation region group data.
 */
function getTranslationBases(
  sequence: ComponentsSequence | GapSequenceWrapper,
  options: MenuChoicesOptions
): {bases: string; group: TranslationRegionGroupData} {
  const {translation} = options;
  assert(translation != null, 'Expected to receive a translation in options.');
  const group = translation.getTranslationRegionGroup(sequence);
  assert(group != null, 'Expected translation region group to be found');
  const bases = group.translations.map((t) => sequence.residueSlice(t.start, t.end)).join('');
  return {bases, group};
}

export function menuChoices(
  sequence: ComponentsSequence | GapSequenceWrapper,
  options: MenuChoicesOptions
): {[choice: string]: MenuChoice | '-'} {
  const selectionLength = sequence.selection().length();
  const isValidCodonOptimization = selectionLength % 3 === 0 && selectionLength !== 0;
  const isRNASequence = sequence.isRNASequence();
  const isInAlignment = hasAlignmentModel(options);

  const showingBaseQualityCasing = (): boolean => {
    if (options.model) {
      const baseQualityOption = options.model
        .get('visibilityOptions')
        .getOption(vo.ALIGNMENT_BASE_QUALITY_CAPITALIZATION.name);
      return baseQualityOption && baseQualityOption.get('visible');
    }
    return false;
  };

  return {
    reindex: {
      name: 'Re-index',
      async click(): Promise<void> {
        await legacyRunModal(ReindexModal, {
          onClickReindex: (current: number, desired: number) => {
            sequence.reindex(current, desired).fail(notifications.notifyError);
          },
          sequenceLength: sequence.length(),
          currentIndex: sequence.selection().start(),
          translationRegionGroups: getTranslationRegionGroupsForSequence(sequence),
        });
      },
      disabled(): boolean {
        const allowCrossOriginParts =
          BenchlingConfig.PARTS_ENABLE_OVERLAPPING_PARTS || BenchlingConfig.PARTS_ENABLE_CROSS_ORIGIN_PARTS;
        return (
          !sequence.allowResidueEdits() ||
          (!allowCrossOriginParts && sequence.selectionContainedInPart()) ||
          sequence.length() === 0
        );
      },
      hide(): boolean {
        return (
          !options.circular ||
          Boolean(options.alignment) ||
          !isFeatureEnabled({feature: MolbioFeature.MOLBIO_EDIT})
        );
      },
    },
    addToFeatureLibrary: {
      name: 'Add to Feature Library',
      click(): void {
        assert(options.annotation != null, 'Expected annotation to be provided in menu options.');
        addToFeatureLibraryHandler(sequence, options.annotation);
      },
      disabled(): boolean {
        return EntityStore.currentUser() == null;
      },
      hide(): boolean {
        return !isFeatureEnabled({feature: MolbioFeature.MOLBIO_ADMIN});
      },
    },
    changeAminoAcid: {
      name: 'Change amino acid',
      async click(): Promise<void> {
        const {translation, mapstate} = options;
        assert(translation != null, 'Expected to receive translation when changing amino acid.');
        let colorScheme = ProteinColorScheme.RASMOL;
        if (!mapstate) {
          assert(false, 'Unexpectedly got null mapstate when checking translation color scheme setting');
        } else {
          colorScheme = mapstate
            .get('visibilityOptions')
            .getOption(vo.TRANSLATIONS.name)
            .getSetting(vo.getTranslationColoringSetting().name)
            .get('value') as ProteinColorScheme;
        }

        const codon = translateCodon(sequence, translation);

        // Render amino acid selector
        const newBases = await legacyRunModal(CodonTableModal, {codon, colorScheme});

        if (!newBases) {
          return;
        }
        const selection = sequence.selection();
        const _sequence = sequence._sequence();
        const bases =
          translation.strand() === -1 ? biojs.util.revcomp(newBases, DNA_COMPLEMENT_TABLE) : newBases;
        const [start, end] = selection.getEndpoints();

        assert(end != null, 'Expected to receive non-null selection end when changing amino acid.');

        if (
          !asm.of(_sequence).canReplace(_sequence, selection, bases, (message: string) => {
            notifications.warn(message);
          })
        ) {
          return;
        }

        // If the sequence is part of an assembly the primers may also need to be updated.
        analytics.track('editor', 'replaced_bases', {source: 'change-amino-acid-modal', bases});
        const xhr = sequence.replace(start, end, bases);

        // Handle xhr success/failure
        xhr.fail((res: Response) => {
          notifications.notifyError(res);
        });
        xhr.client.done(() => {
          selection.setEndpoints(start, end);
        });

        // Explicitly wait for the server to finish (xhr) since didReplace may trigger saves.
        // TODO: Show a loading screen while assembly update is taking place to be "atomic"
        if (asm.of(_sequence).assemblyType != null) {
          Promise.resolve(xhr).then(() => {
            selection.setEndpoints(start, end); // Reset the selection in case the user moved it.
            // Note it's important to do this after updating the selection so that the handler gets
            // the new selection.
            asm.of(_sequence).didReplace(
              _sequence,
              selection,
              bases,
              (message: string) => {
                notifications.success(message);
              },
              (message: string) => {
                notifications.error(message);
              }
            );
          });
        }
      },
      disabled(): boolean {
        const selection = sequence.selection();
        if (
          !sequence.allowResidueEdits() ||
          !sequence.isSelectionEditable() ||
          // This reads oddly but provides type guarantees for later calls.
          !selection.isNotEmpty() ||
          selection.length() > 3 ||
          !options.translation?.selected() ||
          options.translation?.codonTranslationTable(sequence) !== CodonTranslationTable.STANDARD
        ) {
          return true;
        }
        // Should be at most one amino acid since the selection length is 3 or less.
        const selectedAminoAcid = options.translation?.getAminoAcids(
          sequence,
          selection.start(),
          selection.end()
        );
        // Confirm that the amino acid selected is not the start codon of a translation that has its start codon
        // coerced to methionine. `aaIndex` is 1-indexed since it is for display, so check for that instead of 0.
        return (
          options.translation?.coerceStartCodonToMethionine(sequence) &&
          selectedAminoAcid &&
          selectedAminoAcid[0].aaIndex === 1
        );
      },
      hide(): boolean {
        return !isFeatureEnabled({feature: MolbioFeature.MOLBIO_EDIT});
      },
    },
    insertBases: {
      name: 'Insert bases',
      click(): void {
        SequenceMapUtil.showInputBubble(options.polymerMapTabView!);
      },
      disabled(): boolean {
        return !sequence.allowResidueEdits() || !sequence.canInsertAt(sequence.selection().start());
      },
      hide(): boolean {
        if (!options.polymerMapTabView) {
          return true;
        } else {
          return !isFeatureEnabled({feature: MolbioFeature.MOLBIO_EDIT});
        }
      },
    },
    deleteBases: {
      name: 'Delete bases',
      click(): void {
        sequence.deleteSelection();
      },
      disabled(): boolean {
        return !sequence.allowResidueEdits() || !sequence.isSelectionDeletable();
      },
      hide(): boolean {
        return !isFeatureEnabled({feature: MolbioFeature.MOLBIO_EDIT});
      },
    },
    createAnnotation: {
      name: 'Create annotation',
      click(): void {
        trackOpenAnnotationPanel(sequence, 'context_menu_create');
        sequence.selection().trigger('annotation');
      },
      disabled(): boolean {
        return !sequence.allowFeatureEdits();
      },
      hide(): boolean {
        return !isFeatureEnabled({feature: MolbioFeature.MOLBIO_EDIT});
      },
      dataTestAttrs: {component: 'ContextMenu', element: 'createAnnotation'},
    },
    deleteAnnotation: {
      name: 'Delete annotation',
      async click(): Promise<void> {
        assert(options.annotation != null, 'Expected to receive annotation in menu options.');
        trackChoice('deleted_annotation');
        await sequence.deleteFeature(options.annotation);
      },
      disabled(): boolean {
        return !sequence.allowFeatureEdits() || !options.annotation?.editable();
      },
      hide(): boolean {
        return !isFeatureEnabled({feature: MolbioFeature.MOLBIO_EDIT});
      },
    },
    editAnnotation: {
      name: 'Edit annotation',
      click(): void {
        trackOpenAnnotationPanel(sequence, 'context_menu_edit');
        sequence.selection().trigger('annotation', options.annotation);
      },
      disabled(): boolean {
        return (
          !sequence.allowFeatureEdits() || !options.annotation?.editable() || options.annotation?.isNew()
        );
      },
      hide(): boolean {
        return !isFeatureEnabled({feature: MolbioFeature.MOLBIO_EDIT});
      },
    },
    invertSelection: {
      name: 'Invert selection',
      click(): void {
        sequence.selection().invert();
      },
      disabled(): boolean {
        return !sequence.selection().canInvert();
      },
      close(focus: HTMLElement): void {
        $(focus).focus();
      },
      hide(): boolean {
        return !isFeatureEnabled({feature: MolbioFeature.MOLBIO_EDIT});
      },
    },
    copyTranslationGroup: {
      name: getTranslationMenuActionName('Copy AAs for', options.translation),
      copy(): string | void {
        const {bases, group} = getTranslationBases(sequence, options);
        trackChoice('copied_translation_group');
        return util.translateOnStrand(
          bases,
          group.strand,
          sequence.nucleotideType(),
          group.codonTranslationTable,
          group.coerceStartCodonToMethionine,
          true
        );
      },
    },
    copyBasesTranslationGroup: {
      name: getTranslationMenuActionName('Copy bases for', options.translation),
      copy(): string | void {
        const {bases, group} = getTranslationBases(sequence, options);
        trackChoice('copied_bases_translation_group');
        if (group.strand === -1) {
          return util.revcomp(bases, NUCLEOTIDE_TYPE_TO_COMPLEMENT_TABLE[sequence.nucleotideType()]);
        }
        return bases;
      },
    },
    analyzeTranslation: {
      name: 'Analyze translation',
      click(): void {
        assert(options.translation != null, 'Translation to analyze was not provided in options.');
        // Set selection to highlight the translation
        const translationStart = options.translation.start();
        const translationEnd = options.translation.end();
        sequence.selection().setEndpoints(translationStart, translationEnd);
        openOrCreateBiochemicalPropertiesTab(
          sequence,
          options.translation.codonTranslationTable(sequence),
          options.translation.strand(),
          options.translation.coerceStartCodonToMethionine(sequence)
        );
      },
      hide(): boolean {
        return (
          options.mapstate instanceof AlignmentMapState ||
          !isFeatureEnabled({feature: MolbioFeature.MOLBIO_EDIT})
        );
      },
    },
    analyzeSelectionAsTranslation: {
      name: 'Analyze as translation',
      click(): void {
        openOrCreateBiochemicalPropertiesTab(sequence, CodonTranslationTable.STANDARD);
      },
      hide(): boolean {
        return (
          Boolean(options.alignment) ||
          !isFeatureEnabled({feature: MolbioFeature.MOLBIO_EDIT}) ||
          sequence.isOligo()
        );
      },
    },
    deleteTranslation: {
      name: 'Delete this translation region',
      async click(): Promise<void> {
        assert(options.translation != null, 'Translation to delete was not provided in options.');
        trackChoice('deleted_translation');
        await sequence.deleteFeature(options.translation);
        notifications.success('Successfully deleted translation region.');
      },
      disabled(): boolean {
        return !sequence.allowFeatureEdits() || !options.translation?.editable();
      },
      hide(): boolean {
        const group = options.translation?.getTranslationRegionGroup(sequence);
        const hasSingleRegion = !group || group.translations.length === 1;
        return hasSingleRegion || !isFeatureEnabled({feature: MolbioFeature.MOLBIO_EDIT});
      },
    },
    deleteTranslationGroup: {
      name: getTranslationMenuActionName('Delete', options.translation),
      async click(): Promise<void> {
        assert(options.translation != null, 'Translation with group to delete was not provided in options.');
        // TranslationGroupId to be deleted should be a number - only translations hoisted from parts have stringy group ids.
        const groupId = options.translation.translationGroupId() as number;
        if (groupId == null) {
          await sequence.deleteFeature(options.translation);
          notifications.success('Successfully deleted translation.');
          return;
        }
        const tagSchema = sequence.tagSchema();
        if (
          !(await confirmTranslationLinkInvalidationRisk(
            tagSchema,
            options.translation.codonTranslationTable(sequence) !== CodonTranslationTable.STANDARD,
            options.translation.coerceStartCodonToMethionine(sequence),
            true,
            1
          ))
        ) {
          return;
        }
        sequence
          .deleteTranslationGroup(groupId)
          .done(() => {
            notifications.success('Successfully deleted translation group.');
          })
          .fail((res: Response) => {
            notifications.notifyError(res);
          });
        trackChoice('deleted_translation_group');
      },
      disabled(): boolean {
        // The option is disabled if the sequence doesn't allow edits
        // or if any of the translations in the group aren't `editable`.
        if (!sequence.allowFeatureEdits() || !options.translation) {
          return true;
        }
        return !options.translation.getTranslationRegionGroup(sequence)?.editable;
      },
      hide(): boolean {
        return !isFeatureEnabled({feature: MolbioFeature.MOLBIO_EDIT});
      },
    },
    toggleTranslationAA: {
      name: 'Toggle AA positions',
      click(): void {
        assert(options.mapstate != null, 'options.mapstate must be provided to toggle AA positions.');
        const aaOption = options.mapstate.get('visibilityOptions').getOption(vo.AA_INDICES.name);
        aaOption.set({visible: !aaOption.get('visible')});
        trackChoice('toggled_aa_indices');
      },
      hide(): boolean {
        return !isFeatureEnabled({feature: MolbioFeature.MOLBIO_EDIT});
      },
    },
    insertPart: {
      name: 'Insert part',
      click(): void {
        new InsertPartModalView({sequence}).show();
      },
      disabled(): boolean {
        return (
          !sequence.allowResidueEdits() ||
          !sequence.canInsertAt(sequence.selection().start()) ||
          !sequence.canInsertPart()
        );
      },
      hide(): boolean {
        return !isFeatureEnabled({feature: MolbioFeature.MOLBIO_EDIT});
      },
    },
    replacePart: {
      name: 'Replace part',
      click(): void {
        const part = getSinglePart(sequence);
        new InsertPartModalView({sequence, oldPart: part}).show();
      },
      disabled(): boolean {
        return !canDeleteOrReplacePartAtSelectionStart(sequence);
      },
      hide(): boolean {
        return !shouldShowPartMenu(sequence);
      },
    },
    openPart: getOpenPartMenuOption(sequence, null),
    flipPart: {
      name: 'Flip part',
      click(): void {
        assert(
          sequence.selectionContainedInPart(),
          `Expected selection with endpoints ${sequence.selection().start()}, ${sequence
            .selection()
            .end()} to be contained in a part.`
        );
        const part = getSinglePart(sequence);
        const reverse = !part.reversed();
        const complement = !part.complemented();
        sequence.editPart(part, {reverse, complement});
      },
      disabled(): boolean {
        return !canEditPartAtSelectionStart(sequence);
      },
      hide(): boolean {
        return !shouldShowPartMenu(sequence) || sequence.isSingleStranded();
      },
    },
    deletePart: {
      name: 'Delete part',
      click(): void {
        assert(
          sequence.selectionContainedInPart(),
          `Expected selection with endpoints ${sequence.selection().start()}, ${sequence
            .selection()
            .end()} to be contained in a part.`
        );
        const part = getSinglePart(sequence);
        sequence.deletePart(part).fail((res: Response) => {
          notifications.notifyError(res);
        });
      },
      disabled(): boolean {
        return !canDeleteOrReplacePartAtSelectionStart(sequence);
      },
      hide(): boolean {
        return !shouldShowPartMenu(sequence);
      },
    },
    unlinkPart: getUnlinkPartMenuOption(sequence, null),
    setTrim: {
      name: 'Trim...',
      dataTestAttrs: {component: 'ContextMenu', element: 'trim'},
      menu: [
        {
          name: 'To Start',
          click(): void {
            setTrimFromSelection(
              sequence as GapSequenceWrapper,
              (sequence as GapSequenceWrapper).get('trim')!,
              TrimSideOption.START
            );
          },
          disabled(): boolean {
            return !sequence.allowResidueEdits();
          },
        },
        {
          name: 'To End',
          click(): void {
            setTrimFromSelection(
              sequence as GapSequenceWrapper,
              (sequence as GapSequenceWrapper).get('trim')!,
              TrimSideOption.END
            );
          },
          disabled(): boolean {
            return !sequence.allowResidueEdits();
          },
          dataTestAttrs: {component: 'ContextMenu', element: 'trim', key: 'toEnd'},
        },
        {
          name: 'To selection',
          click(): void {
            setTrimFromSelection(
              sequence as GapSequenceWrapper,
              (sequence as GapSequenceWrapper).get('trim')!,
              TrimSideOption.BOTH
            );
          },
          disabled(): boolean {
            return !sequence.allowResidueEdits() || sequence.selection().isEmpty();
          },
        },
        {
          name: 'To start (all sequences)',
          click(): void {
            assert(
              options.tabstate != null,
              'Expected tabstate option to be passed for setting trim on all sequences'
            );
            setAllTrimsFromSelection(sequence as GapSequenceWrapper, options.tabstate, TrimSideOption.START);
          },
          disabled(): boolean {
            return !sequence.allowResidueEdits();
          },
        },
        {
          name: 'To end (all sequences)',
          click(): void {
            assert(
              options.tabstate != null,
              'Expected tabstate option to be passed for setting trim on all sequences'
            );
            setAllTrimsFromSelection(sequence as GapSequenceWrapper, options.tabstate, TrimSideOption.END);
          },
          disabled(): boolean {
            return !sequence.allowResidueEdits();
          },
        },
        {
          name: 'To selection (all sequences)',
          click(): void {
            assert(
              options.tabstate != null,
              'Expected tabstate option to be passed for setting trim on all sequences'
            );
            setAllTrimsFromSelection(sequence as GapSequenceWrapper, options.tabstate, TrimSideOption.BOTH);
          },
          disabled(): boolean {
            return !sequence.allowResidueEdits() || sequence.selection().isEmpty();
          },
        },
      ],
      hide(): boolean {
        return !options.hasTrim || !isFeatureEnabled({feature: MolbioFeature.MOLBIO_EDIT});
      },
    },
    createAlignmentComment: {
      name: 'Create comment',
      click(): void {
        assert(
          options.tabstate != null,
          'Expected tabstate option to be passed for adding alignment comment'
        );
        const comment = new alignmentcomments.AlignmentComment({
          start: sequence.selection().start(),
          end: sequence.selection().end(),
        });
        options.tabstate.get('analysis').get('alignmentComments').add(comment);
        comment.trigger('open', comment);
      },
      hide(): boolean {
        return !isFeatureEnabled({feature: MolbioFeature.MOLBIO_EDIT});
      },
    },
    editAlignmentComment: {
      name: 'Edit comment',
      click(): void {
        assert(options.comment != null, 'Comment to edit was not provided in options.');
        options.comment.trigger('open', options.comment);
      },
      hide(): boolean {
        return !isFeatureEnabled({feature: MolbioFeature.MOLBIO_EDIT});
      },
    },
    deleteAlignmentComment: {
      name: 'Delete comment',
      click(): void {
        assert(options.comment != null, 'Comment to delete was not provided in options.');
        options.comment.destroy();
      },
      hide(): boolean {
        return !isFeatureEnabled({feature: MolbioFeature.MOLBIO_EDIT});
      },
    },
    createTranslation: buildCollapsibleMenu('createTranslation', isInAlignment, sequence, [
      {
        name: 'Forward (all sequences)',
        click(): void {
          (options.tabstate as SequenceAlignmentTabState).createAllTranslation(1);
        },
        disabled(): boolean {
          return !sequence.allowFeatureEdits();
        },
        hide(): boolean {
          return !isInAlignment;
        },
      },
      {
        name: `Reverse (all ${
          isInAlignment && (options.tabstate as SequenceAlignmentTabState)?.containsRnaSequence()
            ? 'DNA '
            : ''
        }sequences)`,
        click(): void {
          (options.tabstate as SequenceAlignmentTabState).createAllTranslation(-1);
        },
        disabled(): boolean {
          return !sequence.allowFeatureEdits();
        },
        hide(): boolean {
          return sequence.isSingleStranded() || !isInAlignment;
        },
        dataTestAttrs: {component: 'ContextMenu', element: 'createTranslation', key: 'reverseAllSequences'},
      },
    ]),
    createProtein: buildCollapsibleMenu('createProtein', isInAlignment, sequence),
    createProteinFromTranslation: (function (): MenuChoice {
      return {
        name: 'Create AA sequence...',
        menu: [
          {
            name: 'All translation regions',
            click(): void {
              assert(options.translation != null, 'Expected to receive a translation in options.');
              sequence.handleCreateProteinFromTranslationClick(options.translation);
            },
            disabled(): boolean {
              return EntityStore.currentUser() == null;
            },
          },
        ],
        disabled(): boolean {
          return EntityStore.currentUser() == null;
        },
        hide(): boolean {
          return !isFeatureEnabled({feature: StandardFeature.CREATE, itemType: ModelTypeConstants.PROTEIN});
        },
      };
    })(),

    toggleCase: {
      name: 'Change case...',
      menu: [
        {
          name: 'To UPPERCASE',
          click(): void {
            toggleCaseHandler(sequence, 'upper');
          },
          disabled(): boolean {
            return (
              !sequence.allowResidueEdits() || !sequence.isSelectionEditable() || showingBaseQualityCasing()
            );
          },
        },
        {
          name: 'To lowercase',
          click(): void {
            toggleCaseHandler(sequence, 'lower');
          },
          disabled(): boolean {
            return (
              !sequence.allowResidueEdits() || !sequence.isSelectionEditable() || showingBaseQualityCasing()
            );
          },
        },
      ],
      hide(): boolean {
        return !isFeatureEnabled({feature: MolbioFeature.MOLBIO_EDIT});
      },
    },
    showPrimer: {
      name: 'Show primer',
      click(): void {
        return sequence.selection().trigger('primer', options.primer);
      },
      disabled(): boolean {
        return options.primer?.id == null;
      },
      hide(): boolean {
        return !isFeatureEnabled({feature: MolbioFeature.MOLBIO_EDIT});
      },
    },
    openPrimer: {
      name: 'Open primer',
      click(): void {
        assert(options.primer != null, 'Primer was not provided in options.');
        FileBrowserActions.openFile(options.primer.get('_primer').get('primer_api_id')!);
      },
      disabled(): boolean {
        return options.primer?.id == null;
      },
      hide(): boolean {
        return !isFeatureEnabled({feature: MolbioFeature.MOLBIO_EDIT});
      },
    },
    editPrimer: {
      name: 'Edit primer',
      click(): void {
        assert(options.primer != null, 'Primer was not provided in options.');
        EditPrimerTabState.edit(options.primer.get('_primer'), sequence);
      },
      disabled(): boolean {
        return !sequence.allowFeatureEdits() || !options.primer?.editable();
      },
      hide(): boolean {
        return !isFeatureEnabled({feature: MolbioFeature.MOLBIO_EDIT});
      },
    },
    deletePrimer: {
      name: 'Detach primer',
      click(): void {
        assert(options.primer != null, 'Primer was not provided in options.');
        const name = options.primer.name();
        options.primer
          .destroy()
          .done(() => {
            sequence.updateVersions();
          })
          .fail((res: Response) => {
            notifications.notifyError(res);
          });
        trackChoice('detached_primer', {name});
      },
      disabled(): boolean {
        const _sequence = sequence._sequence();
        return (
          !sequence.allowFeatureEdits() ||
          !options.primer?.editable() ||
          asm.of(_sequence).isFlankingPrimer(_sequence, options.primer)
        );
      },
      hide(): boolean {
        return !isFeatureEnabled({feature: MolbioFeature.MOLBIO_EDIT});
      },
    },
    primerStructure: {
      name: 'Check primer structure',
      click(): void {
        const selection = sequence.selection();
        if (selection.canPCR()) {
          const fwd = selection.getStartState('primer')!.get('_primer');
          const rev = selection.getEndState('primer')!.get('_primer');
          EditPrimerTabState.editPair(fwd, rev, sequence, true);
        } else {
          assert(options.primer != null, 'Primer was not provided in options.');
          EditPrimerTabState.edit(options.primer.get('_primer'), sequence, true);
        }
      },
      disabled(): boolean {
        return (
          sequence.fileState() == null ||
          ((options.primer != null ? options.primer.id : undefined) == null && !sequence.selection().canPCR())
        );
      },
      hide(): boolean {
        return !isFeatureEnabled({feature: MolbioFeature.MOLBIO_EDIT});
      },
    },
    showEnzyme: {
      name: 'Show enzyme',
      click(): void {
        sequence.selection().trigger('cutsite', options.cutsite);
      },
      hide(): boolean {
        return !isFeatureEnabled({feature: MolbioFeature.MOLBIO_EDIT});
      },
    },
    createFromSelection: {
      name: 'Create DNA sequence',
      id: 'createSequenceContextMenuOption',
      click(): void {
        sequence.handleCreateFromSelectionClick(SequenceType.DOUBLE_STRANDED);
      },
      disabled(): boolean {
        return EntityStore.currentUser() == null;
      },
      hide(): boolean {
        return (
          !isFeatureEnabled({feature: StandardFeature.CREATE, itemType: ModelTypeConstants.SEQUENCE}) ||
          sequence.isOligo()
        );
      },
    },
    createRNAFromSelection: buildCollapsibleMenu('createRNAFromSelection', isInAlignment, sequence),
    optimizeCodons: {
      name: 'Optimize codons',
      menu: [
        {
          name: isValidCodonOptimization ? 'Forward' : "Forward (selection isn't a multiple of 3)",
          click(): void {
            sequence.handleOptimizeCodonsFromSelectionClick(1);
          },
          disabled(): boolean {
            return !isValidCodonOptimization || !isFeatureEnabled({feature: MolbioFeature.MOLBIO_CREATE});
          },
          dataTestAttrs: {component: 'ContextMenu', element: 'optimizeCodons', key: 'forward'},
        },
        {
          name: isValidCodonOptimization ? 'Reverse' : "Reverse (selection isn't a multiple of 3)",
          click(): void {
            sequence.handleOptimizeCodonsFromSelectionClick(-1);
          },
          disabled(): boolean {
            return !isValidCodonOptimization || !isFeatureEnabled({feature: MolbioFeature.MOLBIO_CREATE});
          },
        },
      ],
      hide(): boolean {
        // TODO BNCH-39155
        return !isFeatureEnabled({feature: MolbioFeature.MOLBIO_EDIT}) || isRNASequence;
      },
      dataTestAttrs: {component: 'ContextMenu', element: 'optimizeCodons', key: 'submenu'},
    },
    createPartFromSelection: {
      name: 'Create new part',
      id: 'createPartContextMenuOption',
      click(): void {
        sequence.handleCreatePartFromSelectionClick();
      },
      disabled(): boolean {
        return isCreateNewPartFromSelectionDisabled(sequence);
      },
      hide(): boolean {
        return !isFeatureEnabled({feature: MolbioFeature.MOLBIO_CREATE}) || !sequence.supportsParts();
      },
      dataTestAttrs: {component: 'ContextMenu', element: 'createPart'},
    },
    createPrimer: {
      name: 'Create primer...',
      menu: [
        {
          name: 'Forward',
          click(): void {
            trackCreatePrimer(sequence, 'forward');
            const tab = EditPrimerTabState.createFromSelection(sequence, 1);
            sequence.fileState().buffers().addTab(tab);
          },
          disabled(): boolean {
            return !sequence.allowFeatureEdits();
          },
          dataTestAttrs: {component: 'ContextMenu', element: 'createPrimer', key: 'forward'},
        },
        {
          name: 'Reverse',
          click(): void {
            trackCreatePrimer(sequence, 'reverse');
            const tab = EditPrimerTabState.createFromSelection(sequence, -1);
            sequence.fileState().buffers().addTab(tab);
          },
          disabled(): boolean {
            return !sequence.allowFeatureEdits();
          },
        },
      ],
      hide(): boolean {
        return (
          !isFeatureEnabled({feature: MolbioFeature.MOLBIO_CREATE}) ||
          Boolean(options.alignment) ||
          !sequence.supportsPrimers()
        );
      },
      dataTestAttrs: {component: 'ContextMenu', element: 'createPrimer'},
    },
    linkPrimers: {
      name: 'Link primers',
      click(): void {
        createPrimerPair(sequence);
      },
      disabled(): boolean {
        return !sequence.allowFeatureEdits();
      },
      hide(): boolean {
        return !canCreatePrimerPair(sequence);
      },
    },
    showPair: {
      name: 'Show primer pair',
      click(): void {
        return sequence.selection().trigger('showLinks');
      },
      hide(): boolean {
        return !existingPrimerPairSelected(sequence);
      },
    },
    editPair: {
      name: 'Edit primer pair',
      click(): void {
        const fwd = sequence.selection().getStartState('primer')!.get('_primer');
        const rev = sequence.selection().getEndState('primer')!.get('_primer');
        EditPrimerTabState.editPair(fwd, rev, sequence);
      },
      disabled(): boolean {
        return !sequence.allowFeatureEdits();
      },
      hide(): boolean {
        return !existingPrimerPairSelected(sequence);
      },
    },
    findFlankingPrimers: {
      name: 'Run Primer3',
      click(): void {
        sequence.handlePrimer3FromSelectionClick();
      },
      hide(): boolean {
        return !isFeatureEnabled({feature: MolbioFeature.MOLBIO_EDIT});
      },
      disabled(): boolean {
        return !sequence.allowFeatureEdits();
      },
    },
    copy: {
      name: 'Copy',
      click(): void {
        sequence.handleCopy(getDefaultCopyModeForSequence(sequence));
      },
      disabled(): boolean {
        return !sequence.selection().isCopyable();
      },
      dataTestAttrs: {component: 'ContextMenu', element: 'copy'},
    },

    copySpecial: {
      name: 'Copy special...',
      menu: [
        {
          name: 'Reverse complement',
          click(): void {
            sequence.handleCopy(
              sequence.nucleotideType() === util.NucleotideType.DNA
                ? CopyNucleotideSequenceMode.DNA_REVERSE_COMPLEMENT
                : CopyNucleotideSequenceMode.RNA_REVERSE_COMPLEMENT
            );
          },
          disabled(): boolean {
            return !sequence.selection().isCopyable();
          },
        },
        '-',
        {
          name: 'Translation',
          click(): void {
            sequence.handleCopy(CopyNucleotideSequenceMode.TRANSLATION);
          },
          disabled(): boolean {
            return !sequence.selection().isCopyable();
          },
        },
        {
          name: 'Reverse translation',
          click(): void {
            sequence.handleCopy(CopyNucleotideSequenceMode.REVERSE_COMPLEMENT_TRANSLATION);
          },
          disabled(): boolean {
            return !sequence.selection().isCopyable();
          },
        },
        {
          name: sequence.nucleotideType() === util.NucleotideType.DNA ? 'RNA sequence' : 'DNA sequence',
          click(): void {
            sequence.handleCopy(
              sequence.nucleotideType() === util.NucleotideType.DNA
                ? CopyNucleotideSequenceMode.RNA
                : CopyNucleotideSequenceMode.DNA
            );
          },
          disabled(): boolean {
            return !sequence.selection().isCopyable();
          },
        },
        {
          name:
            sequence.nucleotideType() === util.NucleotideType.DNA
              ? 'RNA reverse complement'
              : 'DNA reverse complement',
          click(): void {
            sequence.handleCopy(
              sequence.nucleotideType() === util.NucleotideType.DNA
                ? CopyNucleotideSequenceMode.RNA_REVERSE_COMPLEMENT
                : CopyNucleotideSequenceMode.DNA_REVERSE_COMPLEMENT
            );
          },
          disabled(): boolean {
            return !sequence.selection().isCopyable();
          },
        },
        {
          name: 'HELM',
          click(): void {
            sequence.handleCopy(CopyNucleotideSequenceMode.HELM);
          },
          disabled(): boolean {
            return (
              !sequence.selection().isCopyable() || sequence.selection().getSelectionResidues().includes('-')
            );
          },
          hide(): boolean {
            return !sequence.supportsChemicalMods();
          },
          dataTestAttrs: {component: 'ContextMenu', element: 'copySpecialHELM'},
        },
      ],
      dataTestAttrs: {component: 'ContextMenu', element: 'copySpecial'},
    },
    '-': '-',
    benchlingBlast: {
      name: BENCHLING_BLAST_MENU_TITLE,
      async click(): Promise<void> {
        await runBLASTModalForSelection(sequence.selection());
      },
      disabled(): boolean {
        return (
          EntityStore.currentUser() == null ||
          !validateBLASTSequence(sequence.selection().getSelectionResidues(), Operator.BLASTN).isValid
        );
      },
      hide(): boolean {
        return sequence.selection().isEmpty() || !BenchlingConfig.ENABLE_BLAST_SEARCH;
      },
    },
    ncbiBlast: {
      name: NCBI_BLAST_MENU_TITLE,
      click(): void {
        if (options.primer != null) {
          // @ts-expect-error
          return blastPrimer(options.primer);
        } else {
          // @ts-expect-error
          return blastSequence(sequence);
        }
      },
      disabled(): boolean {
        return !sequence.selection().isBlastable();
      },
      hide(): boolean {
        return (
          !BenchlingConfig.MOLBIO_ENABLE_EXTERNAL_BLAST ||
          !isFeatureEnabled({feature: MolbioFeature.MOLBIO_EDIT})
        );
      },
    },
    addChemicalModifications: {
      name: 'Add modifications',
      click(): void {
        handleEditFromSelection(sequence, sequence.selection());
      },
      disabled(): boolean {
        return !sequence.allowResidueEdits();
      },
      hide(): boolean {
        return !sequence.supportsChemicalMods();
      },
    },
    editTranslation: {
      name: 'Edit translation',
      click(): void {
        trackOpenTranslationPanel(sequence, null);
        sequence.selection().trigger('translation-panel', null, options.translation);
      },
      disabled(): boolean {
        return (
          !sequence.allowFeatureEdits() ||
          options.translation == null ||
          !options.translation.editable() ||
          options.translation.isNew()
        );
      },
      hide(): boolean {
        return !isFeatureEnabled({feature: MolbioFeature.MOLBIO_EDIT});
      },
    },
  };
}

// ##########################################
// MENUS
// ##########################################

const ANNOTATION_MENU = [
  'editAnnotation',
  'deleteAnnotation',
  'addToFeatureLibrary',
  '-',
  'copy',
  'copySpecial',
  'toggleCase',
  'deleteBases',
  '-',
  'createPartFromSelection',
  'createPrimer',
  'createFromSelection',
  'createRNAFromSelection',
  'createTranslation',
  'createProtein',
  '-',
  'benchlingBlast',
  'ncbiBlast',
  'analyzeSelectionAsTranslation',
];

const COPY_MENU = ['copy', 'copySpecial'];

const CUTSITE_MENU = ['showEnzyme'];

const OLIGO_DNA_MENU = ['insertBases'];

const DNA_MENU = [
  'openPart',
  'flipPart',
  'deletePart',
  'replacePart',
  'unlinkPart',
  '-',
  'reindex',
  'insertBases',
  'insertPart',
  '-',
  'setTrim',
];

const PRIMER_MENU = [
  'showPrimer',
  'openPrimer',
  'editPrimer',
  'deletePrimer',
  'primerStructure',
  '-',
  'copy',
  'copySpecial',
  'toggleCase',
  'deleteBases',
  '-',
  'createPartFromSelection',
  'createFromSelection',
  'createRNAFromSelection',
  'createTranslation',
  'createProtein',
  '-',
  'benchlingBlast',
  'ncbiBlast',
];

const OLIGO_SELECTION_MENU = [
  'copy',
  'copySpecial',
  'toggleCase',
  'deleteBases',
  '-',
  'createAnnotation',
  'addChemicalModifications',
];

function getSelectionMenu(options: {alignment?: boolean} = {alignment: false}): Array<string> {
  let list = [
    'openPart',
    'flipPart',
    'deletePart',
    'replacePart',
    'unlinkPart',
    '-',
    'copy',
    'copySpecial',
    'toggleCase',
    'deleteBases',
    'invertSelection',
    '-',
    'createAlignmentComment',
    'createAnnotation',
    'createPrimer',
    'linkPrimers',
    'showPair',
    'editPair',
    'createPartFromSelection',
    'createFromSelection',
    'createRNAFromSelection',
    'createTranslation',
    'createProtein',
    'addChemicalModifications',
    '-',
    'setTrim',
    '-',
    'findFlankingPrimers',
    'benchlingBlast',
    'ncbiBlast',
    'analyzeSelectionAsTranslation',
    '-',
    'optimizeCodons',
  ];
  if (options.alignment) {
    // eslint-disable-next-line no-restricted-syntax
    list = _.without(list, 'createAnnotation', 'createPrimer', 'findFlankingPrimers');
  } else {
    // eslint-disable-next-line no-restricted-syntax
    list = _.without(list, 'createAlignmentComment', 'setTrim');
  }
  return list;
}

const ALIGNMENT_COMMENT_MENU = ['editAlignmentComment', 'deleteAlignmentComment'];

// Translation menu for the linear and plasmid maps where a translation is more of an annotation.
export const TRANSLATION_BASE_MENU = [
  'copyTranslationGroup',
  'copyBasesTranslationGroup',
  'analyzeTranslation',
  'editTranslation',
  'deleteTranslation',
  'deleteTranslationGroup',
  'createProteinFromTranslation',
  '-',
  'copy',
  'copySpecial',
  'deleteBases',
  'toggleCase',
  '-',
  'benchlingBlast',
  'ncbiBlast',
];

// Translation menu for sequence map where individual codons and AAs are shown.
const TRANSLATION_MENU = ['changeAminoAcid', 'toggleTranslationAA', ...TRANSLATION_BASE_MENU];

export function getMenuChoicesWithPartsSubmenus(
  optionsList: Array<string>,
  sequence: ComponentsSequence,
  options: MenuChoicesOptions
): Array<MenuChoice | '-'> {
  const fullMenu = menuChoices(sequence, options);
  const menu = Array.from(optionsList).map((name) => fullMenu[name]);
  if (optionsList.includes('openPart')) {
    const partsOptions: Array<MenuChoice> = sequence.parts().map((part) => ({
      name: part.name(),
      menu: [
        getSelectPartMenuOption(sequence, part),
        getOpenPartMenuOption(sequence, part),
        getUnlinkPartMenuOption(sequence, part),
      ],
      hide(): boolean {
        return !shouldShowMultiPartMenu(sequence, part);
      },
    }));
    return [...partsOptions, ...menu];
  }
  return menu;
}

function open(
  e: BasicEvent | Event,
  sequence: ComponentsSequence,
  list: Array<string>,
  options: MenuChoicesOptions = {}
): boolean {
  function closeMenu(): void {
    contextmenu.view.close();
  }

  // If there is an existing ongoing XHR that finishes after opening the context menu, it may reset the
  // selection / a model (e.g. annotation) may have been deleted, so the context is no longer valid,
  // e.g. delete bases after selection is reset to a position. To salvage this, we force close the context
  // menu if the selection or any passed in models have changed.
  function listenerBind(bind: boolean): void {
    _.chain(options)
      .values()
      .filter((o) => o instanceof Backbone.Model)
      .uniq()
      .each((model) => {
        if (bind) {
          (model as Backbone.Model).on('destroy remove', closeMenu);
        } else {
          (model as Backbone.Model).off('destroy remove', closeMenu);
        }
      });
    if (bind) {
      sequence.selection().on('change:start change:end', closeMenu);
    } else {
      sequence.selection().off('change:start change:end', closeMenu);
    }
  }

  if (sequence._sequence().canOpenSelectionContextMenu()) {
    listenerBind(true);
    const menu = getMenuChoicesWithPartsSubmenus(list, sequence, options);
    contextmenu.view.once('close', () => {
      listenerBind(false);
    });
    return contextmenu.view.open(e, menu);
  } else {
    e.stopPropagation();
    e.preventDefault();
    return false;
  }
}

export {
  open,
  ANNOTATION_MENU,
  COPY_MENU,
  CUTSITE_MENU,
  DNA_MENU,
  OLIGO_DNA_MENU,
  OLIGO_SELECTION_MENU,
  PRIMER_MENU,
  getSelectionMenu,
  TRANSLATION_MENU,
  ALIGNMENT_COMMENT_MENU,
};
